import {
  GeckoColorScaleThreshold,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoVerticalTankShell,
  GeckoView,
} from 'types'

/**
 * API 653 Table 10.2 - radius tolerances by tank diameter, for radii measured
 * one foot above the shell-to-bottom weld. Each entry applies to diameters
 * below its own.
 */
const RADII_TOLERANCES: ReadonlyArray<{
  belowDiameterFeet: number
  toleranceInches: number
}> = [
  { belowDiameterFeet: 40, toleranceInches: 1 / 2 },
  { belowDiameterFeet: 150, toleranceInches: 3 / 4 },
  { belowDiameterFeet: 250, toleranceInches: 1 },
  { belowDiameterFeet: Number.POSITIVE_INFINITY, toleranceInches: 1.25 },
]

/**
 * API 653: radius tolerances measured more than one foot above the
 * shell-to-bottom weld can be three times the Table 10.2 value
 */
const ABOVE_ONE_FOOT_TOLERANCE_MULTIPLIER = 3

/** API 653: out-of-plumbness must not exceed 1/200th of the shell height */
const PLUMBNESS_TOLERANCE_FRACTION = 1 / 200

/**
 * Returns the radius tolerance for a tank of a given diameter, per API 653
 * Table 10.2
 * @param diameter      The diameter of the tank
 * @param aboveOneFoot  True if the radii were measured more than one foot
 *                      above the shell-to-bottom weld, where the tolerance is
 *                      three times larger
 */
export const getRoundnessTolerance = (
  diameter: GeckoMeasurement,
  aboveOneFoot = false
): GeckoMeasurement => {
  const diameterFeet = diameter.toNumber(GeckoMeasurementUnit.Feet)
  const tolerance =
    RADII_TOLERANCES.find(
      ({ belowDiameterFeet }) => diameterFeet < belowDiameterFeet
    ) ?? RADII_TOLERANCES[RADII_TOLERANCES.length - 1]

  return GeckoMeasurement.CreateFromValue(
    tolerance.toleranceInches *
      (aboveOneFoot ? ABOVE_ONE_FOOT_TOLERANCE_MULTIPLIER : 1),
    GeckoMeasurementUnit.Inches
  )
}

/** Returns the out-of-plumbness tolerance for a shell height, per API 653 */
export const getPlumbnessTolerance = (
  shellHeight: GeckoMeasurement
): GeckoMeasurement => shellHeight.scale(PLUMBNESS_TOLERANCE_FRACTION)

/**
 * Table 10.2 applies at this height above the shell-to-bottom weld. Above this
 * height, the tolerances are three times larger.
 */
const TABLE_MEASUREMENT_HEIGHT = GeckoMeasurement.CreateFromValue(
  1,
  GeckoMeasurementUnit.Feet
)

/**
 * Returns a view's tolerance for a given shell and a given height on that
 * shell. Returns undefined if the shell does not have the dimension the
 * formula needs.
 *
 * An undefined height means "anywhere on the shell". A formula that changes
 * with height then returns its largest value - the value a single whole-layer
 * number (such as a color scale threshold) must use.
 */
type ToleranceFormula = (
  shell: GeckoVerticalTankShell,
  heightAboveBottom: GeckoMeasurement | undefined
) => GeckoMeasurement | undefined

const roundnessFormula: ToleranceFormula = (shell, heightAboveBottom) =>
  shell.radius.num > 0
    ? getRoundnessTolerance(
        shell.radius.scale(2),
        // Only the first foot of the shell uses the table value directly
        !heightAboveBottom || heightAboveBottom.gt(TABLE_MEASUREMENT_HEIGHT)
      )
    : undefined

const plumbnessFormula: ToleranceFormula = (shell) =>
  shell.height.num > 0 ? getPlumbnessTolerance(shell.height) : undefined

/**
 * The rollout views that have an API 653 tolerance, each with the derived
 * layer that holds only its out-of-tolerance values. Best fit cylinder
 * rollouts are not included: the standard gives no tolerance for them, so
 * they keep the generic default threshold and get no derived layer.
 */
const TOLERANCE_LAYERS: ReadonlyArray<{
  sourceView: GeckoView
  exceedanceView: GeckoView
  formula: ToleranceFormula
}> = [
  {
    sourceView: GeckoView.RoundnessRolloutMean,
    exceedanceView: GeckoView.RoundnessRolloutMeanExceedance,
    formula: roundnessFormula,
  },
  {
    sourceView: GeckoView.RoundnessRolloutPeak,
    exceedanceView: GeckoView.RoundnessRolloutPeakExceedance,
    formula: roundnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessTopBottomRolloutMean,
    exceedanceView: GeckoView.PlumbnessTopBottomRolloutMeanExceedance,
    formula: plumbnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessTopBottomRolloutPeak,
    exceedanceView: GeckoView.PlumbnessTopBottomRolloutPeakExceedance,
    formula: plumbnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessBestFitRolloutMean,
    exceedanceView: GeckoView.PlumbnessBestFitRolloutMeanExceedance,
    formula: plumbnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessBestFitRolloutPeak,
    exceedanceView: GeckoView.PlumbnessBestFitRolloutPeakExceedance,
    formula: plumbnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessRolloutMean,
    exceedanceView: GeckoView.PlumbnessRolloutMeanExceedance,
    formula: plumbnessFormula,
  },
  {
    sourceView: GeckoView.PlumbnessRolloutPeak,
    exceedanceView: GeckoView.PlumbnessRolloutPeakExceedance,
    formula: plumbnessFormula,
  },
]

const EXCEEDANCE_VIEW_BY_SOURCE_VIEW_ID: ReadonlyMap<string, GeckoView> =
  new Map(
    TOLERANCE_LAYERS.map(({ sourceView, exceedanceView }) => [
      sourceView.id,
      exceedanceView,
    ])
  )

// A derived layer and its source use the same tolerance formula
const TOLERANCE_FORMULAS: ReadonlyMap<string, ToleranceFormula> = new Map(
  TOLERANCE_LAYERS.flatMap(({ sourceView, exceedanceView, formula }) => [
    [sourceView.id, formula] as const,
    [exceedanceView.id, formula] as const,
  ])
)

/**
 * Returns the derived layer that holds this view's out-of-tolerance values.
 * Returns undefined if the view has no tolerance or is a derived layer itself.
 */
export const getExceedanceView = (
  view: GeckoView | undefined
): GeckoView | undefined =>
  view ? EXCEEDANCE_VIEW_BY_SOURCE_VIEW_ID.get(view.id) : undefined

/**
 * Returns the permitted deviation for this view at a given height on the
 * shell. Returns undefined if the view has no tolerance, if the component is
 * not a vertical tank shell, or if the shell has no usable dimensions. The
 * caller then falls back to a generic default.
 *
 * The result is computed on each call, not stored, so a correction to the
 * tank dimensions changes the tolerance immediately.
 * @param view              The view that is displayed
 * @param componentModel    The model of the component the data was measured on
 * @param heightAboveBottom The height of the reading on the shell, where that
 *                          changes the tolerance. Omit to get the whole-layer
 *                          tolerance.
 */
export const getDeviationTolerance = (
  view: GeckoView | undefined,
  componentModel: GeckoComponentModel | undefined,
  heightAboveBottom?: GeckoMeasurement
): GeckoMeasurement | undefined => {
  const formula = view && TOLERANCE_FORMULAS.get(view.id)
  if (
    !formula ||
    componentModel?.type !== GeckoComponentModelType.VerticalTankShell
  ) {
    return undefined
  }

  const tolerance = formula(componentModel, heightAboveBottom)?.convertTo(
    GeckoMeasurementUnit.Inches
  )
  return !tolerance || !Number.isFinite(tolerance.num) || tolerance.num <= 0
    ? undefined
    : tolerance
}

/**
 * Returns the symmetric color scale threshold for a view whose tolerance comes
 * from the asset dimensions. A threshold applies to the full layer, so this
 * uses the largest tolerance when the tolerance changes with height.
 * @param view            The view that is displayed
 * @param componentModel  The model of the component the data was measured on
 */
export const getDeviationToleranceThreshold = (
  view: GeckoView | undefined,
  componentModel: GeckoComponentModel | undefined
): GeckoColorScaleThreshold | undefined => {
  const tolerance = getDeviationTolerance(view, componentModel)
  if (!tolerance) return undefined

  return GeckoColorScaleThreshold.CreateFromValues(
    -tolerance.num,
    0,
    tolerance.num,
    GeckoMeasurementUnit.Inches
  )
}
